import React, { useEffect, useState } from "react";

import { RefreshCcw } from "lucide-react";

import { isLongLoadingIntegration } from "../../constants";
import { getCrawlerPreferenceLoadingText } from "../../utils";

import { LoadingState } from "./LoadingState";
import { StatusCard } from "./StatusCard";

const LONG_LOADING_TIMEOUT_SECONDS = 30;

interface LongLoadingStateProps {
  sourceId: string;
  sourceLabel: string;
}

export function LongLoadingState({
  sourceId,
  sourceLabel,
}: LongLoadingStateProps) {
  const [_loadingTime, setLoadingTime] = useState(0);
  const isLongLoading = isLongLoadingIntegration(sourceId);
  const [shouldShowLongMessage, setShouldShowLongMessage] =
    useState(isLongLoading);

  const { heading, description } = getCrawlerPreferenceLoadingText(sourceId);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (!isLongLoading) {
      interval = setInterval(() => {
        setLoadingTime((prev) => {
          const newTime = prev + 1;
          if (
            newTime >= LONG_LOADING_TIMEOUT_SECONDS &&
            !shouldShowLongMessage
          ) {
            setShouldShowLongMessage(true);
          }
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isLongLoading, shouldShowLongMessage]);

  if (!shouldShowLongMessage) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingState message="Fetching preferences..." />
      </div>
    );
  }

  return (
    <StatusCard status="success">
      <p className="text-sm text-zinc-900 dark:text-white">
        Your <span className="font-semibold">{sourceLabel}</span> account is now
        connected.
      </p>
      <div className="flex gap-2.5 items-start">
        <div className="shrink-0 w-6 h-6">
          <RefreshCcw className="w-5 h-5 animate-spin text-zinc-900 dark:text-white" />
        </div>
        <div className="text-sm text-zinc-600 dark:text-zinc-300 leading-[23px]">
          <p className="font-semibold mb-0">{heading}</p>
          <p>{description}</p>
        </div>
      </div>
    </StatusCard>
  );
}
